import { BaseService } from './BaseService';
import { apiService } from './apiService';
import {
  UserFavorite,
  UserFavoriteInput,
  UserFavoritesResponse,
  UserFavoriteFilter,
  UserFavoriteCheckResponse,
  UserFavoriteCountResponse,
  UserFavoritePayload
} from '../types/userFavorites';
import { ApiResponse } from '../types/api';

interface FavoritesCache {
  items: {
    data: UserFavoritesResponse | null;
    timestamp: number | null;
  };
  itemDetails: Map<string, {
    data: UserFavorite;
    timestamp: number;
  }>;
  checks: Map<string, {
    data: UserFavoriteCheckResponse;
    timestamp: number;
  }>;
  counts: Map<string, {
    count: number;
    timestamp: number;
  }>;
}

const CACHE_TTL = {
  ITEMS: 30000,       // 30 seconds
  DETAILS: 60000,     // 1 minute
  CHECKS: 30000,      // 30 seconds
  COUNTS: 30000       // 30 seconds
};

export class UserFavoritesService extends BaseService {
  private cache: FavoritesCache = {
    items: {
      data: null,
      timestamp: null
    },
    itemDetails: new Map(),
    checks: new Map(),
    counts: new Map()
  };

  constructor() {
    super('UserFavoritesService', { debug: false });
  }

  private isCacheExpired(timestamp: number | null, ttl: number): boolean {
    if (!timestamp) return true;
    return Date.now() - timestamp > ttl;
  }

  private getCacheKey(kind: string, itemId: string): string {
    return `${kind}:${itemId}`;
  }

  /**
   * Create a new favorite
   */
  async createFavorite(input: UserFavoriteInput): Promise<UserFavorite> {
    return this.executeOperation(async () => {
      this.logDebug('Creating favorite', { kind: input.kind, itemId: input.payload.itemId });

      const response = await apiService.post<UserFavorite | ApiResponse<UserFavorite>>(
        '/api/favorites',
        input
      );

      let favorite: UserFavorite;

      // Handle different response formats
      if ('data' in response && response.data) {
        favorite = response.data;
      } else if ('id' in response) {
        favorite = response as UserFavorite;
      } else {
        throw new Error('Invalid response format from create favorite API');
      }

      // Update caches
      this.invalidateCache();
      this.cache.itemDetails.set(favorite.id, {
        data: favorite,
        timestamp: Date.now()
      });

      // Update check cache
      const checkKey = this.getCacheKey(favorite.kind, favorite.payload.itemId);
      this.cache.checks.set(checkKey, {
        data: { isFavorited: true, favoriteId: favorite.id },
        timestamp: Date.now()
      });

      this.logDebug('Favorite created successfully', { id: favorite.id });
      return favorite;
    }, 'createFavorite');
  }

  /**
   * Get favorites with pagination
   */
  async getFavorites(filter?: UserFavoriteFilter): Promise<UserFavoritesResponse> {
    return this.executeOperation(async () => {
      const { kind, limit = 20, continuationToken } = filter || {};

      this.logDebug('Getting favorites', { kind, limit, hasContinuationToken: !!continuationToken });

      const params = new URLSearchParams();
      if (kind) params.append('kind', kind);
      if (limit) params.append('limit', limit.toString());
      if (continuationToken) params.append('continuationToken', continuationToken);

      const response = await apiService.get<UserFavoritesResponse | ApiResponse<UserFavoritesResponse>>(
        `/api/favorites?${params.toString()}`
      );

      let result: UserFavoritesResponse;

      // Handle different response formats
      if ('items' in response && Array.isArray(response.items)) {
        result = response as UserFavoritesResponse;
      } else if ('data' in response && response.data) {
        result = response.data;
      } else {
        result = { items: [], hasMore: false };
      }

      // Update item details cache
      result.items.forEach(item => {
        this.cache.itemDetails.set(item.id, {
          data: item,
          timestamp: Date.now()
        });
      });

      this.logDebug('Favorites loaded successfully', { count: result.items.length, hasMore: result.hasMore });
      return result;
    }, 'getFavorites');
  }

  /**
   * Get a single favorite by ID
   */
  async getFavorite(id: string): Promise<UserFavorite> {
    return this.executeOperation(async () => {
      // Check cache first
      const cached = this.cache.itemDetails.get(id);
      if (cached && !this.isCacheExpired(cached.timestamp, CACHE_TTL.DETAILS)) {
        this.logDebug('Returning cached favorite', { id });
        return cached.data;
      }

      this.logDebug('Getting favorite', { id });

      const response = await apiService.get<UserFavorite | ApiResponse<UserFavorite>>(
        `/api/favorites/${id}`
      );

      let favorite: UserFavorite;

      if ('data' in response && response.data) {
        favorite = response.data;
      } else if ('id' in response) {
        favorite = response as UserFavorite;
      } else {
        throw new Error(`Favorite with ID ${id} not found`);
      }

      // Update cache
      this.cache.itemDetails.set(id, {
        data: favorite,
        timestamp: Date.now()
      });

      this.logDebug('Favorite loaded successfully', { id });
      return favorite;
    }, 'getFavorite');
  }

  /**
   * Update a favorite's payload
   */
  async updateFavorite(id: string, payload: UserFavoritePayload): Promise<UserFavorite> {
    return this.executeOperation(async () => {
      this.logDebug('Updating favorite', { id });

      const response = await apiService.put<UserFavorite | ApiResponse<UserFavorite>>(
        `/api/favorites/${id}`,
        { payload }
      );

      let favorite: UserFavorite;

      if ('data' in response && response.data) {
        favorite = response.data;
      } else if ('id' in response) {
        favorite = response as UserFavorite;
      } else {
        throw new Error('Invalid response format from update favorite API');
      }

      // Update caches
      this.cache.itemDetails.set(id, {
        data: favorite,
        timestamp: Date.now()
      });
      this.invalidateCache();

      this.logDebug('Favorite updated successfully', { id });
      return favorite;
    }, 'updateFavorite');
  }

  /**
   * Delete a favorite (hard delete - permanent)
   */
  async deleteFavorite(id: string): Promise<void> {
    return this.executeOperation(async () => {
      this.logDebug('Deleting favorite', { id });

      // Get the favorite first to update check cache
      const cached = this.cache.itemDetails.get(id);
      
      await apiService.delete(`/api/favorites/${id}`);

      // Update caches
      this.cache.itemDetails.delete(id);
      this.invalidateCache();

      // Update check cache if we had the item cached
      if (cached?.data) {
        const checkKey = this.getCacheKey(cached.data.kind, cached.data.payload.itemId);
        this.cache.checks.set(checkKey, {
          data: { isFavorited: false, favoriteId: null },
          timestamp: Date.now()
        });
      }

      this.logDebug('Favorite deleted successfully', { id });
    }, 'deleteFavorite');
  }

  /**
   * Check if an item is favorited
   */
  async checkFavorite(kind: string, itemId: string): Promise<UserFavoriteCheckResponse> {
    return this.executeOperation(async () => {
      const cacheKey = this.getCacheKey(kind, itemId);

      // Check cache first
      const cached = this.cache.checks.get(cacheKey);
      if (cached && !this.isCacheExpired(cached.timestamp, CACHE_TTL.CHECKS)) {
        this.logDebug('Returning cached check result', { kind, itemId });
        return cached.data;
      }

      this.logDebug('Checking favorite', { kind, itemId });

      const params = new URLSearchParams({ kind, itemId });
      const response = await apiService.get<UserFavoriteCheckResponse>(
        `/api/favorites/check?${params.toString()}`
      );

      // Update cache
      this.cache.checks.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      });

      this.logDebug('Check complete', { kind, itemId, isFavorited: response.isFavorited });
      return response;
    }, 'checkFavorite');
  }

  /**
   * Get favorites count
   */
  async getCount(kind?: string): Promise<number> {
    return this.executeOperation(async () => {
      const cacheKey = kind || 'all';

      // Check cache first
      const cached = this.cache.counts.get(cacheKey);
      if (cached && !this.isCacheExpired(cached.timestamp, CACHE_TTL.COUNTS)) {
        this.logDebug('Returning cached count', { kind, count: cached.count });
        return cached.count;
      }

      this.logDebug('Getting favorites count', { kind });

      const params = new URLSearchParams();
      if (kind) params.append('kind', kind);

      const response = await apiService.get<UserFavoriteCountResponse>(
        `/api/favorites/count?${params.toString()}`
      );

      const count = response.count || 0;

      // Update cache
      this.cache.counts.set(cacheKey, {
        count,
        timestamp: Date.now()
      });

      this.logDebug('Count retrieved', { kind, count });
      return count;
    }, 'getCount');
  }

  /**
   * Toggle favorite status (add if not favorited, remove if favorited)
   */
  async toggleFavorite(
    kind: string,
    itemId: string,
    itemData?: Partial<UserFavoritePayload>
  ): Promise<{ isFavorited: boolean; favoriteId: string | null }> {
    return this.executeOperation(async () => {
      this.logDebug('Toggling favorite', { kind, itemId });

      // Check current status
      const checkResult = await this.checkFavorite(kind, itemId);

      if (checkResult.isFavorited && checkResult.favoriteId) {
        // Remove from favorites
        await this.deleteFavorite(checkResult.favoriteId);
        return { isFavorited: false, favoriteId: null };
      } else {
        // Add to favorites
        const payload: UserFavoritePayload = {
          itemId,
          title: itemData?.title || itemId,
          ...itemData,
          metadata: {
            ...itemData?.metadata,
          },
        };

        const favorite = await this.createFavorite({ kind, payload });
        return { isFavorited: true, favoriteId: favorite.id };
      }
    }, 'toggleFavorite');
  }

  // Cache management methods
  private invalidateCache(): void {
    this.cache.items = {
      data: null,
      timestamp: null
    };
    this.cache.counts.clear();
  }

  clearCache(): void {
    this.logDebug('Clearing favorites cache');
    this.cache.items = {
      data: null,
      timestamp: null
    };
    this.cache.itemDetails.clear();
    this.cache.checks.clear();
    this.cache.counts.clear();
  }

  getCacheStatus(): {
    itemsCached: boolean;
    itemDetailsCount: number;
    checksCount: number;
    countsCount: number;
  } {
    return {
      itemsCached: this.cache.items.data !== null,
      itemDetailsCount: this.cache.itemDetails.size,
      checksCount: this.cache.checks.size,
      countsCount: this.cache.counts.size
    };
  }

  protected onCircuitBreakerChange(eventType: string, data: any): void {
    this.logDebug(`Circuit breaker event: ${eventType}`, data);
  }

  protected onRetry(error: Error, attempt: number, delay: number, operationName: string): void {
    this.logDebug(`Retrying ${operationName}`, {
      attempt,
      delay,
      error: error.message
    });
  }
}

export const userFavoritesService = new UserFavoritesService();

// Debug helpers for development
if (typeof window !== 'undefined') {
  (window as any).userFavoritesServiceDebug = {
    service: userFavoritesService,
    getCacheStatus: () => userFavoritesService.getCacheStatus(),
    clearCache: () => userFavoritesService.clearCache()
  };
}
